import { computed, ref } from 'vue';
import { Building, Room, Roomgroup } from '@/models';
import { instanceToInstance, plainToInstance } from 'class-transformer';
import { usageTypeList } from '@/enums/roomgroup';
import { Address } from '@/models/types/Address';
import { useBuildingStore } from '@/store/entities/buildingStore';
import { useInspectedBuildingForm } from '@/composables/useInspectedBuildingForm';
import { useOrderStore } from '@/store/order/orderStore';
import { useInspectedBuildingStore } from '@/store/entities/inspectedBuildingStore';

export function useBuildingForm(
  node: Building | undefined,
  parentId?: string,
  imageCaptureRef?: any,
  emit?: any
) {
  const building = ref(new Building());
  const roomGroup = ref(new Roomgroup());
  const room = ref(new Room());
  const isEditMode = ref(false);
  const buildingStore = useBuildingStore();

  const isOnSiteInspection = computed(() =>
    useOrderStore().isOnSiteInspection()
  );

  const buildingId = computed(() => (node ? node.id : building.value.id || ''));

  const existingInspectedBuilding = computed(() => {
    return useInspectedBuildingStore().getInspectedBuildingByBuildingId(
      buildingId.value
    );
  });

  const { inspectedBuilding, initializeInspectedBuildingForm } =
    useInspectedBuildingForm(existingInspectedBuilding.value, buildingId.value);

  const initializeBuildingForm = () => {
    if (!(node instanceof Building)) {
      isEditMode.value = false;
      building.value.businessEntityId = parentId;
      setupNewBuilding();
      if (isOnSiteInspection.value || useOrderStore().isSmartHeatingOrder())
        initializeInspectedBuildingForm();
      return;
    }

    isEditMode.value = true;
    building.value = instanceToInstance(node);
    building.value.address = plainToInstance(Address, building.value.address);
    if (isOnSiteInspection.value || useOrderStore().isSmartHeatingOrder())
      initializeInspectedBuildingForm();
  };

  const setupNewBuilding = () => {
    building.value.ordinal = buildingStore.getNewOrdinal();

    roomGroup.value.buildingId = building.value.id;
    roomGroup.value.usageType = usageTypeList[2].value;
    roomGroup.value.ordinal = 0;

    room.value.roomGroupId = roomGroup.value.id;
    room.value.usageType = 'HALLWAY_AND_STAIRCASE';
    room.value.floorLevel = { type: 'GROUND' };
    room.value.ordinal = 0;
  };

  const handleSubmit = () => {
    if (isEditMode.value) {
      emit('update-entities', [building.value]);
    } else {
      emit('update-entities', [
        building.value,
        {
          entity: roomGroup.value,
          payload: { suppressEntityCrudEvent: true },
        },
        room.value,
      ]);
    }
    if (imageCaptureRef.value) {
      imageCaptureRef.value.saveImage();
    }

    if (isOnSiteInspection.value || useOrderStore().isSmartHeatingOrder()) {
      emit('update-entities', [inspectedBuilding.value]);
    }
  };

  const handleIsAccessibleChange = (isAccessible: boolean) => {
    building.value.isAccessible = isAccessible;
    if (isAccessible) {
      building.value.reasonNotAccessible = undefined;
      building.value.dateNotAccessible = undefined;
      building.value.typeNotAccessible = undefined;
    } else {
      building.value.dateNotAccessible = new Date().toISOString();
    }
  };

  const handleReasonChange = (reason: string) => {
    building.value.reasonNotAccessible = reason;
    building.value.typeNotAccessible = undefined;
    building.value.dateNotAccessible = new Date().toISOString();
  };

  return {
    building,
    inspectedBuilding,
    roomGroup,
    room,
    isEditMode,
    initializeBuildingForm,
    handleIsAccessibleChange,
    handleReasonChange,
    handleSubmit,
  };
}
